import React, { MouseEventHandler, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { IntlContext, type IntlShape } from 'react-intl';
import { Button, Tooltip, UncontrolledTooltip } from 'reactstrap';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { v4 as uuidv4 } from 'uuid';
import { TypedMemo } from '../../types';
import { SMLButtonProps } from './props';
import { LoadingIcon } from '../LoadingIcon';
import { titleCaseLabel } from './titleCaseLabel';

const SMLButtonComponent: React.FC<React.PropsWithChildren<SMLButtonProps>> = ({
  children,
  color = 'primary',
  className,
  disabled = false,
  onClick,
  testID = '',
  doNotPassEvent = false,
  size = 'md',
  outline = false,
  leftIcon,
  rightIcon,
  isLoading = false,
  error,
  stretch = false,
  omitCap = false,
  submit = false,
  tooltip,
  hoverTooltip,
  ariaLabel,
}) => {
  // The label is title-cased only in English (QA litr2-3). No provider (a bare test) counts as English.
  const locale = (useContext(IntlContext) as IntlShape | null | undefined)?.locale ?? 'en';
  const uniqueId = useMemo(() => `smlbutton-${uuidv4()}`, []);
  const [tooltipText, setTooltipText] = useState<string | undefined>();

  const handleOnClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    (event) => {
      if (!onClick || submit) {
        return;
      }
      onClick(doNotPassEvent ? undefined : event);
    },
    [doNotPassEvent, onClick, submit]
  );

  const onTooltipClick = useCallback(() => {
    setTooltipText(undefined);
  }, []);

  useEffect(() => {
    if (!error && !tooltip) {
      return;
    }
    setTooltipText(error?.message ?? tooltip);
  }, [error, tooltip]);

  const hasChildren = React.Children.count(children) !== 0;

  return (
    <>
      <Button
        id={uniqueId}
        disabled={disabled || isLoading}
        onClick={handleOnClick}
        color={color}
        size={size}
        outline={outline}
        type={submit ? 'submit' : 'button'}
        aria-label={ariaLabel}
        aria-busy={isLoading || undefined}
        className={clsx(className, testID, {
          outline,
          'w-100': stretch,
        })}
      >
        {!isLoading && leftIcon !== undefined && (
          <FontAwesomeIcon icon={leftIcon} className={clsx({ 'me-2': hasChildren })} />
        )}
        {isLoading && (
          <>
            <LoadingIcon small />
            {hasChildren ? ' ' : null}
          </>
        )}
        {/* The span keeps the label an element React owns. Google Translate replaces bare text
            nodes with <font>, and inserting the spinner before such a node throws NotFoundError. */}
        {hasChildren && (
          <span>{typeof children === 'string' && !omitCap ? titleCaseLabel(children, locale) : children}</span>
        )}
        {!isLoading && rightIcon !== undefined && (
          <FontAwesomeIcon icon={rightIcon} className={clsx({ 'ms-2': hasChildren })} />
        )}
      </Button>
      <Tooltip
        target={uniqueId}
        isOpen={tooltipText !== undefined}
        className={clsx({ 'sml-button-tooltip-error': !!error })}
        onClick={onTooltipClick}
        role={error ? 'alert' : undefined}
      >
        {tooltipText}
      </Tooltip>
      {hoverTooltip !== undefined && hoverTooltip !== '' && (
        <UncontrolledTooltip target={uniqueId} trigger="hover focus">
          {hoverTooltip}
        </UncontrolledTooltip>
      )}
    </>
  );
};

export const SMLButton = TypedMemo(SMLButtonComponent);
